<template>
  <div class="alarm-level-config">
    <div class="alarm-level-config__panel">
      <el-form :inline="true" :model="query" size="small" class="alarm-level-config__search">
        <el-form-item label="配置名称">
          <el-input v-model.trim="query.configName" clearable placeholder="请输入配置名称" @keyup.enter.native="handleSearch" />
        </el-form-item>
        <el-form-item label="启用状态">
          <el-select v-model="query.enabled" clearable placeholder="全部">
            <el-option label="启用" :value="1" />
            <el-option label="停用" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item class="alarm-level-config__search-actions">
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <div class="alarm-level-config__toolbar">
        <div>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openAddDialog">新增</el-button>
          <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="loadList">刷新</el-button>
        </div>
        <strong>分级报警配置</strong>
      </div>

      <el-table
        v-loading="loading"
        :data="data"
        border
        stripe
        height="520"
        empty-text="暂无分级报警配置"
      >
        <el-table-column type="index" label="序号" width="70" align="center" :index="indexMethod" />
        <el-table-column label="配置名称" prop="configName" min-width="170" show-overflow-tooltip />
        <el-table-column label="一级分钟" prop="level1Minutes" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag type="warning" size="small">{{ row.level1Minutes }}分钟</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="二级分钟" prop="level2Minutes" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag type="danger" size="small">{{ row.level2Minutes }}分钟</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="三级分钟" prop="level3Minutes" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag class="alarm-level-config__level3" size="small">{{ row.level3Minutes }}分钟</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="一级处理人" prop="level1RecipientUserNames" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.level1RecipientUserNames || "-" }}</template>
        </el-table-column>
        <el-table-column label="二级处理人" prop="level2RecipientUserNames" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.level2RecipientUserNames || "-" }}</template>
        </el-table-column>
        <el-table-column label="三级处理人" prop="level3RecipientUserNames" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.level3RecipientUserNames || "-" }}</template>
        </el-table-column>
        <el-table-column label="启用状态" prop="enabled" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="isEnabled(row.enabled) ? 'success' : 'info'" size="small">
              {{ isEnabled(row.enabled) ? "启用" : "停用" }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" prop="updateTime" width="170" align="center" show-overflow-tooltip />
        <el-table-column label="备注" prop="remark" min-width="180" show-overflow-tooltip />
        <el-table-column label="操作" fixed="right" width="190" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" icon="el-icon-edit" @click="openEditDialog(row)">编辑</el-button>
            <el-button type="text" size="small" @click="handleEnabled(row)">
              {{ isEnabled(row.enabled) ? "停用" : "启用" }}
            </el-button>
            <el-button type="text" size="small" class="alarm-level-config__danger" icon="el-icon-delete" @click="handleDelete(row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="alarm-level-config__pagination">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page.sync="page.currentPage"
          :page-size.sync="page.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="page.total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="820px"
      append-to-body
      destroy-on-close
      :close-on-click-modal="false"
      @closed="resetForm"
    >
      <el-form ref="configForm" :model="form" :rules="rules" label-width="126px" size="small" class="alarm-level-config__form">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="配置名称" prop="configName">
              <el-input v-model.trim="form.configName" maxlength="100" show-word-limit placeholder="请输入配置名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="启用状态" prop="enabled">
              <el-switch v-model="form.enabled" :active-value="1" :inactive-value="0" active-text="启用" inactive-text="停用" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="一级分钟" prop="level1Minutes">
              <el-input-number v-model="form.level1Minutes" :min="1" :precision="0" controls-position="right" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="二级分钟" prop="level2Minutes">
              <el-input-number v-model="form.level2Minutes" :min="1" :precision="0" controls-position="right" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="三级分钟" prop="level3Minutes">
              <el-input-number v-model="form.level3Minutes" :min="1" :precision="0" controls-position="right" />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="一级处理人" prop="level1RecipientUserIds">
              <div class="alarm-level-config__recipient-field">
                <div class="alarm-level-config__recipient-tags" @click="openRecipientDialog('level1')">
                  <el-tag
                    v-for="item in getSelectedUsers('level1')"
                    :key="'level1-' + item.id"
                    closable
                    size="small"
                    @close.stop="removeRecipient('level1', item.id)"
                  >
                    {{ formatUserLabel(item) }}
                  </el-tag>
                  <span v-if="!recipientModels.level1.length" class="alarm-level-config__recipient-placeholder">请选择一级处理人</span>
                </div>
                <el-button size="small" icon="el-icon-user" @click="openRecipientDialog('level1')">选择</el-button>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="二级处理人" prop="level2RecipientUserIds">
              <div class="alarm-level-config__recipient-field">
                <div class="alarm-level-config__recipient-tags" @click="openRecipientDialog('level2')">
                  <el-tag
                    v-for="item in getSelectedUsers('level2')"
                    :key="'level2-' + item.id"
                    closable
                    size="small"
                    @close.stop="removeRecipient('level2', item.id)"
                  >
                    {{ formatUserLabel(item) }}
                  </el-tag>
                  <span v-if="!recipientModels.level2.length" class="alarm-level-config__recipient-placeholder">请选择二级处理人（可选）</span>
                </div>
                <el-button size="small" icon="el-icon-user" @click="openRecipientDialog('level2')">选择</el-button>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="三级处理人" prop="level3RecipientUserIds">
              <div class="alarm-level-config__recipient-field">
                <div class="alarm-level-config__recipient-tags" @click="openRecipientDialog('level3')">
                  <el-tag
                    v-for="item in getSelectedUsers('level3')"
                    :key="'level3-' + item.id"
                    closable
                    size="small"
                    @close.stop="removeRecipient('level3', item.id)"
                  >
                    {{ formatUserLabel(item) }}
                  </el-tag>
                  <span v-if="!recipientModels.level3.length" class="alarm-level-config__recipient-placeholder">请选择三级处理人（可选）</span>
                </div>
                <el-button size="small" icon="el-icon-user" @click="openRecipientDialog('level3')">选择</el-button>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="备注" prop="remark">
              <el-input v-model.trim="form.remark" type="textarea" :rows="3" maxlength="500" show-word-limit placeholder="请输入备注" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" icon="el-icon-check" :loading="submitLoading" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="recipientDialog.title"
      :visible.sync="recipientDialog.visible"
      width="920px"
      append-to-body
      :close-on-click-modal="false"
      @closed="resetRecipientDialog"
    >
      <div class="alarm-level-config__recipient-picker">
        <div class="alarm-level-config__dept-pane">
          <el-input
            v-model.trim="deptFilterText"
            size="small"
            clearable
            prefix-icon="el-icon-search"
            placeholder="搜索部门"
          />
          <el-tree
            ref="deptTree"
            v-loading="deptLoading"
            class="alarm-level-config__dept-tree"
            :data="deptTree"
            node-key="id"
            highlight-current
            default-expand-all
            :props="deptTreeProps"
            :filter-node-method="filterDeptNode"
            @node-click="handleDeptNodeClick"
          />
        </div>
        <div class="alarm-level-config__user-pane">
          <div class="alarm-level-config__user-search">
            <el-input
              v-model.trim="recipientQuery.keyword"
              size="small"
              clearable
              prefix-icon="el-icon-search"
              placeholder="输入账号或姓名"
              @keyup.enter.native="handleRecipientSearch"
              @clear="handleRecipientSearch"
            />
            <el-button size="small" type="primary" icon="el-icon-search" @click="handleRecipientSearch">查询</el-button>
            <el-button size="small" icon="el-icon-refresh-left" @click="resetRecipientSearch">重置</el-button>
          </div>
          <el-table
            ref="recipientUserTable"
            v-loading="recipientLoading"
            :data="recipientUsers"
            border
            stripe
            height="340"
            row-key="id"
            empty-text="暂无用户"
            @select="handleRecipientTableSelect"
            @select-all="handleRecipientTableSelectAll"
          >
            <el-table-column type="selection" width="50" reserve-selection />
            <el-table-column label="姓名" prop="realName" min-width="120" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.realName || row.name || "-" }}</template>
            </el-table-column>
            <el-table-column label="账号" prop="account" min-width="130" show-overflow-tooltip />
            <el-table-column label="部门" prop="deptName" min-width="180" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.deptName || "-" }}</template>
            </el-table-column>
          </el-table>
          <div class="alarm-level-config__user-pagination">
            <el-pagination
              small
              background
              layout="total, sizes, prev, pager, next"
              :current-page.sync="recipientPage.currentPage"
              :page-size.sync="recipientPage.pageSize"
              :page-sizes="[10, 20, 50, 100]"
              :total="recipientPage.total"
              @size-change="handleRecipientSizeChange"
              @current-change="handleRecipientCurrentChange"
            />
          </div>
        </div>
      </div>
      <div class="alarm-level-config__selected-bar">
        <span class="alarm-level-config__selected-label">已选：</span>
        <el-tag
          v-for="item in recipientDialog.selectedUsers"
          :key="'selected-' + item.id"
          closable
          size="small"
          @close="removeRecipientDialogUser(item.id)"
        >
          {{ formatUserLabel(item) }}
        </el-tag>
        <span v-if="!recipientDialog.selectedUsers.length" class="alarm-level-config__recipient-placeholder">暂无</span>
      </div>
      <span slot="footer">
        <el-button size="small" @click="recipientDialog.visible = false">取消</el-button>
        <el-button size="small" @click="clearRecipientDialogSelection">清空</el-button>
        <el-button type="primary" size="small" icon="el-icon-check" @click="confirmRecipientDialog">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { add, changeEnabled, getDeptTree, getDetail, getList, getUserList, remove, update } from "./api";

const emptyForm = () => ({
  id: "",
  configName: "",
  level1Minutes: 10,
  level2Minutes: 30,
  level3Minutes: 60,
  level1RecipientUserIds: "",
  level1RecipientUserNames: "",
  level2RecipientUserIds: "",
  level2RecipientUserNames: "",
  level3RecipientUserIds: "",
  level3RecipientUserNames: "",
  enabled: 1,
  remark: "",
});

const recipientFields = {
  level1: {
    ids: "level1RecipientUserIds",
    names: "level1RecipientUserNames",
  },
  level2: {
    ids: "level2RecipientUserIds",
    names: "level2RecipientUserNames",
  },
  level3: {
    ids: "level3RecipientUserIds",
    names: "level3RecipientUserNames",
  },
};

export default {
  name: "system-alarm-level-config",
  data() {
    return {
      loading: false,
      submitLoading: false,
      query: {
        configName: "",
        enabled: "",
      },
      page: {
        currentPage: 1,
        pageSize: 10,
        total: 0,
      },
      data: [],
      dialogVisible: false,
      dialogTitle: "新增分级报警配置",
      form: emptyForm(),
      recipientModels: {
        level1: [],
        level2: [],
        level3: [],
      },
      userOptions: [],
      userMap: {},
      deptLoading: false,
      deptFilterText: "",
      deptTree: [],
      deptTreeProps: {
        label: "title",
        children: "children",
      },
      recipientDialog: {
        visible: false,
        level: "",
        title: "选择处理人",
        selectedIds: [],
        selectedUsers: [],
      },
      recipientQuery: {
        deptId: "",
        keyword: "",
      },
      recipientPage: {
        currentPage: 1,
        pageSize: 10,
        total: 0,
      },
      recipientUsers: [],
      recipientLoading: false,
      syncingRecipientTable: false,
      rules: {
        configName: [
          { required: true, message: "请输入配置名称", trigger: "blur" },
          { min: 1, max: 100, message: "配置名称不能超过100个字符", trigger: "blur" },
        ],
        level1Minutes: [
          { required: true, message: "请输入一级分钟", trigger: "change" },
          { validator: (rule, value, callback) => this.validatePositive(value, callback), trigger: "change" },
        ],
        level2Minutes: [
          { required: true, message: "请输入二级分钟", trigger: "change" },
          { validator: this.validateLevel2Minutes, trigger: "change" },
        ],
        level3Minutes: [
          { required: true, message: "请输入三级分钟", trigger: "change" },
          { validator: this.validateLevel3Minutes, trigger: "change" },
        ],
        level1RecipientUserIds: [
          { required: true, message: "请选择一级处理人", trigger: "change" },
        ],
        remark: [
          { max: 500, message: "备注不能超过500个字符", trigger: "blur" },
        ],
      },
    };
  },
  created() {
    this.loadList();
  },
  watch: {
    deptFilterText(value) {
      if (this.$refs.deptTree) {
        this.$refs.deptTree.filter(value);
      }
    },
    recipientUsers() {
      this.syncRecipientTableSelection();
    },
  },
  methods: {
    loadList() {
      this.loading = true;
      const params = this.cleanParams({
        ...this.query,
        page: this.page.currentPage,
        size: this.page.pageSize,
      });
      getList(params).then((res) => {
        if (res.data && res.data.success === false) {
          this.$message.error(res.data.msg || "查询失败");
          return;
        }
        const payload = this.normalizePayload(res.data);
        this.data = payload.records || [];
        this.page.total = Number(payload.total || 0);
      }).catch(() => {
      }).finally(() => {
        this.loading = false;
      });
    },
    handleSearch() {
      this.page.currentPage = 1;
      this.loadList();
    },
    handleReset() {
      this.query = {
        configName: "",
        enabled: "",
      };
      this.page.currentPage = 1;
      this.loadList();
    },
    handleSizeChange(size) {
      this.page.pageSize = size;
      this.page.currentPage = 1;
      this.loadList();
    },
    handleCurrentChange(current) {
      this.page.currentPage = current;
      this.loadList();
    },
    openAddDialog() {
      this.dialogTitle = "新增分级报警配置";
      this.form = emptyForm();
      this.recipientModels = {
        level1: [],
        level2: [],
        level3: [],
      };
      this.dialogVisible = true;
    },
    openEditDialog(row) {
      if (!row || !row.id) {
        this.$message.warning("缺少配置ID");
        return;
      }
      this.dialogTitle = "编辑分级报警配置";
      this.submitLoading = true;
      getDetail(row.id).then((res) => {
        if (res.data && res.data.success === false) {
          this.$message.error(res.data.msg || "查询详情失败");
          return;
        }
        this.form = {
          ...emptyForm(),
          ...this.normalizePayload(res.data),
        };
        this.hydrateRecipients();
        this.dialogVisible = true;
      }).finally(() => {
        this.submitLoading = false;
      });
    },
    submitForm() {
      this.syncRecipientFields();
      this.$refs.configForm.validate((valid) => {
        if (!valid) {
          return;
        }
        const payload = {
          ...this.form,
          level1Minutes: Number(this.form.level1Minutes),
          level2Minutes: Number(this.form.level2Minutes),
          level3Minutes: Number(this.form.level3Minutes),
          enabled: Number(this.form.enabled),
        };
        const request = payload.id ? update : add;
        this.submitLoading = true;
        request(payload).then((res) => {
          if (res.data && res.data.success === false) {
            this.$message.error(res.data.msg || "保存失败");
            return;
          }
          this.$message.success("保存成功");
          this.dialogVisible = false;
          this.loadList();
        }).finally(() => {
          this.submitLoading = false;
        });
      });
    },
    handleDelete(row) {
      if (!row || !row.id) {
        this.$message.warning("缺少配置ID");
        return;
      }
      this.$confirm(`确认删除配置“${row.configName || row.id}”？`, "提示", {
        type: "warning",
      }).then(() => {
        this.loading = true;
        return remove(row.id).then((res) => {
          if (res.data && res.data.success === false) {
            this.$message.error(res.data.msg || "删除失败");
            return;
          }
          this.$message.success("删除成功");
          this.loadList();
        });
      }).catch(() => {
      }).finally(() => {
        this.loading = false;
      });
    },
    handleEnabled(row) {
      if (!row || !row.id) {
        this.$message.warning("缺少配置ID");
        return;
      }
      const enabled = this.isEnabled(row.enabled) ? 0 : 1;
      const actionText = enabled ? "启用" : "停用";
      this.$confirm(`确认${actionText}配置“${row.configName || row.id}”？`, "提示", {
        type: "warning",
      }).then(() => {
        this.loading = true;
        return changeEnabled(row.id, enabled).then((res) => {
          if (res.data && res.data.success === false) {
            this.$message.error(res.data.msg || `${actionText}失败`);
            return;
          }
          this.$message.success(`${actionText}成功`);
          this.loadList();
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    openRecipientDialog(level) {
      this.syncRecipientFields();
      const field = recipientFields[level];
      this.recipientDialog = {
        visible: true,
        level,
        title: `选择${this.getLevelName(level)}处理人`,
        selectedIds: this.splitValue(this.form[field.ids]),
        selectedUsers: this.getSelectedUsers(level),
      };
      this.recipientQuery.keyword = "";
      this.recipientPage.currentPage = 1;
      this.$nextTick(() => {
        const afterDeptLoaded = () => {
          this.ensureDefaultDept();
          this.loadRecipientUsers();
        };
        if (this.deptTree.length) {
          afterDeptLoaded();
        } else {
          this.loadDeptTree().then(afterDeptLoaded);
        }
      });
    },
    loadDeptTree() {
      this.deptLoading = true;
      return getDeptTree().then((res) => {
        const list = this.normalizePayload(res.data);
        this.deptTree = this.normalizeDeptTree(Array.isArray(list) ? list : []);
      }).finally(() => {
        this.deptLoading = false;
      });
    },
    normalizeDeptTree(list) {
      return (list || []).map((item) => {
        const id = String(item.id || item.value || item.key || "");
        return {
          ...item,
          id,
          value: String(item.value || id),
          title: item.title || item.deptName || item.label || item.name || id,
          children: this.normalizeDeptTree(item.children || []),
        };
      });
    },
    ensureDefaultDept() {
      if (!this.recipientQuery.deptId && this.deptTree.length) {
        this.recipientQuery.deptId = String(this.deptTree[0].id);
      }
      this.$nextTick(() => {
        if (this.$refs.deptTree && this.recipientQuery.deptId) {
          this.$refs.deptTree.setCurrentKey(this.recipientQuery.deptId);
        }
      });
    },
    filterDeptNode(value, data) {
      if (!value) {
        return true;
      }
      return String(data.title || data.deptName || "").indexOf(value) !== -1;
    },
    handleDeptNodeClick(data) {
      this.recipientQuery.deptId = data && data.id ? String(data.id) : "";
      this.recipientPage.currentPage = 1;
      this.loadRecipientUsers();
    },
    handleRecipientSearch() {
      this.recipientPage.currentPage = 1;
      this.loadRecipientUsers();
    },
    resetRecipientSearch() {
      this.recipientQuery.keyword = "";
      this.recipientPage.currentPage = 1;
      this.loadRecipientUsers();
    },
    loadRecipientUsers() {
      this.recipientLoading = true;
      const keywordText = String(this.recipientQuery.keyword || "").trim();
      const baseParams = {
        current: this.recipientPage.currentPage,
        size: this.recipientPage.pageSize,
        deptId: this.recipientQuery.deptId,
      };
      const requests = keywordText
        ? [
          getUserList(this.cleanParams({
            ...baseParams,
            account: keywordText,
          })),
          getUserList(this.cleanParams({
            ...baseParams,
            realName: keywordText,
          })),
        ]
        : [getUserList(baseParams)];
      Promise.all(requests.map((request) => request.catch(() => null))).then((responses) => {
        const users = [];
        let total = 0;
        responses.forEach((res) => {
          if (res) {
            const payload = this.normalizeUserPage(res.data);
            total += Number(payload.total || 0);
            users.push(...(payload.records || []));
          }
        });
        const merged = this.mergeUsersById(users);
        this.recipientUsers = merged;
        this.recipientPage.total = requests.length > 1 ? merged.length : total;
        this.mergeUserOptions(merged);
      }).finally(() => {
        this.recipientLoading = false;
      });
    },
    handleRecipientSizeChange(size) {
      this.recipientPage.pageSize = size;
      this.recipientPage.currentPage = 1;
      this.loadRecipientUsers();
    },
    handleRecipientCurrentChange(current) {
      this.recipientPage.currentPage = current;
      this.loadRecipientUsers();
    },
    handleRecipientTableSelect(selection, row) {
      if (this.syncingRecipientTable || !row) {
        return;
      }
      const id = String(row.id);
      const checked = selection.some((item) => String(item.id) === id);
      if (checked) {
        this.addRecipientDialogUser(row);
      } else {
        this.removeRecipientDialogUser(id);
      }
    },
    handleRecipientTableSelectAll(selection) {
      if (this.syncingRecipientTable) {
        return;
      }
      if (selection.length) {
        selection.forEach((row) => this.addRecipientDialogUser(row));
      } else {
        this.recipientUsers.forEach((row) => this.removeRecipientDialogUser(row.id));
      }
    },
    addRecipientDialogUser(user) {
      if (!user || !user.id) {
        return;
      }
      const id = String(user.id);
      const normalized = {
        ...user,
        id,
      };
      if (!this.recipientDialog.selectedIds.includes(id)) {
        this.recipientDialog.selectedIds.push(id);
      }
      const index = this.recipientDialog.selectedUsers.findIndex((item) => String(item.id) === id);
      if (index === -1) {
        this.recipientDialog.selectedUsers.push(normalized);
      } else {
        this.$set(this.recipientDialog.selectedUsers, index, normalized);
      }
      this.mergeUserOptions([normalized]);
    },
    removeRecipientDialogUser(id) {
      const userId = String(id);
      this.recipientDialog.selectedIds = this.recipientDialog.selectedIds.filter((item) => String(item) !== userId);
      this.recipientDialog.selectedUsers = this.recipientDialog.selectedUsers.filter((item) => String(item.id) !== userId);
      this.syncRecipientTableSelection();
    },
    clearRecipientDialogSelection() {
      this.recipientDialog.selectedIds = [];
      this.recipientDialog.selectedUsers = [];
      this.syncRecipientTableSelection();
    },
    syncRecipientTableSelection() {
      this.$nextTick(() => {
        const table = this.$refs.recipientUserTable;
        if (!table) {
          return;
        }
        this.syncingRecipientTable = true;
        table.clearSelection();
        const selectedIds = this.recipientDialog.selectedIds.map((id) => String(id));
        this.recipientUsers.forEach((row) => {
          if (selectedIds.includes(String(row.id))) {
            table.toggleRowSelection(row, true);
          }
        });
        this.$nextTick(() => {
          this.syncingRecipientTable = false;
        });
      });
    },
    confirmRecipientDialog() {
      const level = this.recipientDialog.level;
      if (!level) {
        return;
      }
      this.recipientModels[level] = this.recipientDialog.selectedIds.map((id) => String(id));
      this.mergeUserOptions(this.recipientDialog.selectedUsers);
      this.handleRecipientChange(level);
      this.recipientDialog.visible = false;
    },
    resetRecipientDialog() {
      this.recipientDialog = {
        visible: false,
        level: "",
        title: "选择处理人",
        selectedIds: [],
        selectedUsers: [],
      };
      this.recipientUsers = [];
      this.recipientQuery.keyword = "";
      this.deptFilterText = "";
    },
    removeRecipient(level, id) {
      this.recipientModels[level] = (this.recipientModels[level] || []).filter((item) => String(item) !== String(id));
      this.handleRecipientChange(level);
    },
    getSelectedUsers(level) {
      return (this.recipientModels[level] || []).map((id) => {
        const user = this.userMap[String(id)];
        return user || {
          id: String(id),
          realName: String(id),
          account: "",
          deptName: "",
        };
      });
    },
    getLevelName(level) {
      const names = {
        level1: "一级",
        level2: "二级",
        level3: "三级",
      };
      return names[level] || "";
    },
    handleRecipientChange(level) {
      const field = recipientFields[level];
      const ids = this.recipientModels[level] || [];
      this.form[field.ids] = ids.join(",");
      this.form[field.names] = ids.map((id) => {
        const user = this.userMap[String(id)] || {};
        return user.realName || user.name || user.account || "";
      }).filter(Boolean).join(",");
      if (level === "level1" && this.$refs.configForm) {
        this.$refs.configForm.validateField("level1RecipientUserIds");
      }
    },
    hydrateRecipients() {
      Object.keys(recipientFields).forEach((level) => {
        const field = recipientFields[level];
        const ids = this.splitValue(this.form[field.ids]);
        const names = this.splitValue(this.form[field.names]);
        this.recipientModels[level] = ids;
        ids.forEach((id, index) => {
          if (!this.userMap[id]) {
            const user = {
              id,
              realName: names[index] || id,
              account: "",
              deptName: "",
            };
            this.$set(this.userMap, id, user);
            this.userOptions.push(user);
          }
        });
      });
    },
    syncRecipientFields() {
      Object.keys(recipientFields).forEach((level) => {
        this.handleRecipientChange(level);
      });
    },
    resetForm() {
      this.form = emptyForm();
      this.recipientModels = {
        level1: [],
        level2: [],
        level3: [],
      };
      if (this.$refs.configForm) {
        this.$refs.configForm.clearValidate();
      }
    },
    validatePositive(value, callback) {
      const numberValue = Number(value);
      if (!Number.isFinite(numberValue) || numberValue <= 0) {
        callback(new Error("分钟数必须大于0"));
        return;
      }
      callback();
    },
    validateLevel2Minutes(rule, value, callback) {
      const level1 = Number(this.form.level1Minutes);
      const level2 = Number(value);
      if (!Number.isFinite(level2) || level2 <= 0) {
        callback(new Error("分钟数必须大于0"));
        return;
      }
      if (level2 <= level1) {
        callback(new Error("二级分钟必须大于一级分钟"));
        return;
      }
      callback();
    },
    validateLevel3Minutes(rule, value, callback) {
      const level2 = Number(this.form.level2Minutes);
      const level3 = Number(value);
      if (!Number.isFinite(level3) || level3 <= 0) {
        callback(new Error("分钟数必须大于0"));
        return;
      }
      if (level3 <= level2) {
        callback(new Error("三级分钟必须大于二级分钟"));
        return;
      }
      callback();
    },
    normalizePayload(payload) {
      if (payload && payload.data && payload.data.data) {
        return payload.data.data;
      }
      if (payload && payload.data) {
        return payload.data;
      }
      return payload || {};
    },
    normalizeUserList(payload) {
      return this.normalizeUserPage(payload).records;
    },
    normalizeUserPage(payload) {
      const data = this.normalizePayload(payload);
      if (Array.isArray(data)) {
        return {
          records: data,
          total: data.length,
        };
      }
      if (data && Array.isArray(data.records)) {
        return {
          records: data.records,
          total: data.total || data.records.length,
        };
      }
      return {
        records: [],
        total: 0,
      };
    },
    mergeUsersById(users) {
      const map = {};
      (users || []).forEach((item) => {
        if (!item || !item.id) {
          return;
        }
        map[String(item.id)] = {
          ...item,
          id: String(item.id),
        };
      });
      return Object.keys(map).map((id) => map[id]);
    },
    mergeUserOptions(users) {
      (users || []).forEach((item) => {
        if (!item || !item.id) {
          return;
        }
        const id = String(item.id);
        const user = {
          ...item,
          id,
        };
        this.$set(this.userMap, id, user);
        const index = this.userOptions.findIndex((option) => String(option.id) === id);
        if (index === -1) {
          this.userOptions.push(user);
        } else {
          this.$set(this.userOptions, index, user);
        }
      });
    },
    splitValue(value) {
      if (Array.isArray(value)) {
        return value.map((item) => String(item)).filter(Boolean);
      }
      return String(value || "").split(",").map((item) => item.trim()).filter(Boolean);
    },
    cleanParams(params) {
      const result = {};
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          result[key] = params[key];
        }
      });
      return result;
    },
    formatUserLabel(user) {
      const name = user.realName || user.name || user.account || user.id;
      const account = user.account ? `/${user.account}` : "";
      const dept = user.deptName ? `（${user.deptName}）` : "";
      return `${name}${account}${dept}`;
    },
    isEnabled(enabled) {
      return Number(enabled) === 1;
    },
    indexMethod(index) {
      return (this.page.currentPage - 1) * this.page.pageSize + index + 1;
    },
  },
};
</script>

<style scoped lang="scss">
.alarm-level-config {
  padding: 12px;

  &__panel {
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    overflow: hidden;
  }

  &__search {
    padding: 14px 16px 2px;
    border-bottom: 1px solid #ebeef5;
  }

  &__search-actions {
    margin-right: 0;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 50px;
    padding: 0 16px;

    strong {
      color: #303133;
      font-size: 15px;
    }
  }

  &__pagination {
    display: flex;
    justify-content: flex-end;
    padding: 10px 16px;
    border-top: 1px solid #ebeef5;
  }

  &__danger {
    color: #f56c6c;
  }

  &__level3 {
    color: #c45656;
    background: #fef0f0;
    border-color: #fab6b6;
  }

  &__recipient-field {
    display: flex;
    align-items: stretch;
    gap: 8px;
  }

  &__recipient-tags {
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    align-items: center;
    min-height: 32px;
    padding: 3px 8px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    cursor: pointer;
    gap: 6px;
  }

  &__recipient-placeholder {
    color: #c0c4cc;
    font-size: 13px;
  }

  &__recipient-picker {
    display: grid;
    grid-template-columns: 260px minmax(0, 1fr);
    gap: 12px;
    min-height: 420px;
  }

  &__dept-pane,
  &__user-pane {
    min-width: 0;
  }

  &__dept-pane {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 10px;
  }

  &__dept-tree {
    height: 374px;
    margin-top: 8px;
    overflow: auto;
  }

  &__user-search {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;

    .el-input {
      flex: 1;
    }
  }

  &__user-pagination {
    display: flex;
    justify-content: flex-end;
    padding-top: 8px;
  }

  &__selected-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    min-height: 32px;
    margin-top: 12px;
    gap: 6px;
  }

  &__selected-label {
    color: #606266;
    font-size: 13px;
  }
}

.alarm-level-config__form {
  .el-input-number {
    width: 100%;
  }
}

@media (max-width: 992px) {
  .alarm-level-config {
    &__toolbar {
      align-items: stretch;
      flex-direction: column;
      gap: 10px;
      padding: 12px 16px;
    }

    &__recipient-picker {
      grid-template-columns: 1fr;
    }
  }
}
</style>
